import { createRouter, createWebHistory } from 'vue-router';
import { authUser, deleteCookie, dispatchSystemError, getCache, removeCache, ROLES } from '@/helpers';
// import { logoutUser } from '@/System/Store/Api/ApiUser';

const routes = [
    {
        path: '/',
        name: 'login',
        component: () => import('../Views/Login.vue'),
        meta: {
            requiresAuth: false,
            title: 'Login',
        },
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: () => import('../Views/ResetPassword.vue'),
        meta: {
            requiresAuth: false,
            title: 'Återställ lösenord',
        },
        beforeEnter: (to) => {
            if (to.params.token.length <= 0) {
                return { name: '404' };
            }
        },
        props(route) {
            return {
                token: route.params.token,
            };
        },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../Views/Register/Choice.vue'),
        meta: {
            requiresAuth: false,
            title: 'Välj affärslösning',
        },
        beforeEnter: () => {
            if (authUser()) {
                return { path: '/' };
            }
        },
    },
    {
        path: '/login/complete-profile',
        name: 'login-complete-profile',
        component: () => import('../Views/MissingUserDetails.vue'),
        meta: {
            requiresAuth: true,
            title: 'Färdigställning av ditt konto',
        },
    },
    {
        path: '/account',
        name: 'account',
        component: () => import('../Views/Account.vue'),
        meta: {
            requiresAuth: true,
            title: 'Mitt konto',
        },
    },
    {
        path: '/department',
        name: 'department',
        component: () => import('../Views/Departments/Users.vue'),
        meta: {
            requiresAuth: true,
            title: 'Användare',
        },
    },
    {
        path: '/department/payment',
        name: 'payment',
        component: () => import('../Views/Departments/Payment.vue'),
        meta: {
            requiresAuth: true,
            title: 'Abonnemang och betalning',
        },
    },
    {
        path: '/department/settings',
        name: 'settings',
        component: () => import('../Views/Departments/Settings.vue'),
        meta: {
            requiresAuth: true,
            title: 'Företagsuppgifter',
        },
    },
    {
        path: '/department/users',
        name: 'users',
        component: () => import('../Views/Departments/Users.vue'),
        meta: {
            requiresAuth: true,
            title: 'Användare',
        },
    },
    {
        path: '/department/competencies',
        name: 'competencies',
        component: () => import('../Views/Departments/Competences/Competences.vue'),
        meta: {
            requiresAuth: true,
            title: 'Kompetenser',
        },
    },
    {
        path: '/department/clients',
        name: 'clients',
        component: () => import('../Views/Departments/Clients.vue'),
        meta: {
            requiresAuth: true,
            title: 'Kunder',
        },
    },
    {
        path: '/certificate',
        name: 'certificate',
        component: () => import('../Views/Certificate/Certificate.vue'),
        meta: {
            requiresAuth: true,
            title: 'Utfärdade certifikat',
        },
    },
    {
        path: '/certificate/create',
        name: 'create-certificate',
        component: () => import('../Views/Certificate/create/CreateCertificate.vue'),
        meta: {
            requiresAuth: true,
            title: 'Skapa certifikat',
        },
    },
    {
        path: '/certificate/single/:certificateToken/user',
        name: 'single-certificate-user',
        component: () => import('../Views/Certificate/User'),
        meta: {
            requiresAuth: true,
            title: 'Visa certifierad användare',
        },
        props(route) {
            return {
                certificateToken: route.params.certificateToken,
            };
        },
    },
    {
        path: '/certificate/single/:certificateToken',
        name: 'single-certificate',
        component: () => import('../Views/Certificate/SingleCertificate.vue'),
        meta: {
            requiresAuth: true,
            title: 'Enskilt certifikat',
        },
        beforeEnter: (to) => {
            if (to.params.certificateToken.length <= 0) {
                return { name: '404' };
            }
        },
        props(route) {
            return {
                id: route.params.certificateToken,
            };
        },
    },
    {
        path: '/certificate/verify/:certificateToken/:signature',
        name: 'verify-certificate',
        component: () => import('../Views/Certificate/VerifySingleCertificate'),
        meta: {
            requiresAuth: false,
            title: 'Verifiera certifikat',
        },
        props(route) {
            return {
                id: route.params.certificateToken,
                signature: route.params.signature,
            };
        },
    },
    {
        path: '/certificate/report',
        name: 'certificate-report',
        component: () => import('../Views/Certificate/CertificateReport.vue'),
        meta: {
            requiresAuth: true,
            title: 'Skapa rapport',
        },
    },
    {
        path: '/edit-users',
        name: 'edit-users',
        component: () => import('../Views/EditUsers.vue'),
        meta: {
            requiresAuth: true,
            title: 'Hantera användare',
        },
    },
    {
        path: '/accept-department-invite/:token/:profile_id/:id',
        name: 'accept-department-invite',
        component: () => import('@views/AcceptInvitation/Index.vue'),
        props(route) {
            return {
                token: route.params.token,
                profileId: route.params.profile_id,
                id: route.params.id,
            };
        },
        meta: {
            requiresAuth: false,
            title: 'Acceptera inbjudan',
        },
    },
    {
        path: '/id06-consent',
        name: 'id06-consent',
        component: () => import('../Views/ID06Consent.vue'),
        meta: {
            requiresAuth: true,
            title: 'Samtycke till behandling av personuppgifter i ID06 Kompetensdatabas',
        },
    },
    {
        path: '/superadmin',
        name: 'superadmin',
        component: () => import('../Views/Superadmin/Home.vue'),
        meta: {
            requiresAuth: true,
            title: 'Superadmin',
        },
    },
    {
        path: '/superadmin/certificates/create',
        name: 'superadmin-create-certificate',
        component: () => import('../Views/Superadmin/cerificate-create/CreateCertificate.vue'),
        meta: {
            requiresAuth: true,
            title: 'Superadmin - Skapa certifikat',
        },
    },
    {
        path: '/superadmin/competences',
        name: 'superadmin-competences',
        component: () => import('../Views/Superadmin/competences/Competences.vue'),
        meta: {
            requiresAuth: true,
            title: 'Superadmin - Hantera kompetenser',
        },
    },
    {
        path: '/superadmin/departments/:id/certificates',
        name: 'superadmin-department-certificates',
        component: () => import('../Views/Superadmin/departments/Certificates.vue'),
        props(route) {
            return {
                id: route.params.id,
            };
        },
        meta: {
            requiresAuth: true,
            title: 'Superadmin - Hantera certifikat',
        },
    },
    {
        path: '/superadmin/departments/:id/profiles',
        name: 'superadmin-department-profiles',
        component: () => import('../Views/Superadmin/departments/Profiles.vue'),
        props(route) {
            return {
                id: route.params.id,
            };
        },
        meta: {
            requiresAuth: true,
            title: 'Superadmin - Hantera profiles',
        },
    },
    {
        path: '/:catchAll(.*)*',
        name: '404',
        component: () => import('../Views/404.vue'),
        meta: {
            title: '404 | Sidan hittades inte',
        },
    },
    {
        path: '/educate',
        children: [
            {
                path: 'select-plan',
                name: 'educate-select-plan',
                component: () => import('../Views/Register/SelectPlan.vue'),
                meta: {
                    title: 'Educate - Välj abonnemang',
                },
            },
            {
                path: 'register',
                name: 'educate-register',
                component: () => import('../Views/Register/Register.vue'),
                meta: {
                    title: 'Educate - Registrera',
                },
            },
        ],
    },
    {
        path: '/employ',
        name: 'employ',
        children: [
            {
                path: 'select-plan',
                name: 'employ-select-plan',
                component: () => import('../Views/Employ/Register/SelectPlan.vue'),
                meta: {
                    title: 'Employ - Välj abonnemang',
                },
            },
            {
                path: 'select-plan-invite',
                name: 'employ-select-plan-invite',
                component: () => import('../Views/Employ/Register/SelectPlanInvite.vue'),
                meta: {
                    title: 'Employ - Registrera er för 3 månaders gratis tillgång till Employ Plus',
                },
            },
            {
                path: 'register',
                name: 'employ-register',
                component: () => import('../Views/Employ/Register/Register.vue'),
                meta: {
                    title: 'Employ - Register',
                },
            },
            {
                path: 'dashboard',
                name: 'employ-dashboard',
                component: () => import('../Views/Employ/Dashboard/Dashboard.vue'),
                meta: {
                    title: 'Dashboard',
                    requiresAuth: true,
                },
            },
            {
                path: 'certificate',
                name: 'employ-certificate',
                component: () => import('../Views/Employ/Certificate/Certificate.vue'),
                meta: {
                    title: 'Utfärdade certifikat',
                    requiresAuth: true,
                },
            },
            {
                path: 'certificate/create',
                name: 'employ-create-certificate',
                component: () => import('../Views/Employ/Certificate/CreateCertificate.vue'),
                meta: {
                    title: 'Utfärda certifikat',
                    requiresAuth: true,
                },
            },
            {
                path: 'certificate/report',
                name: 'employ-certificate-report',
                component: () => import('../Views/Employ/Certificate/CertificateReport.vue'),
                meta: {
                    title: 'Skapa rapport',
                    requiresAuth: true,
                },
            },
            {
                path: 'certificate/single/:certificateToken',
                name: 'employ-internal-single-certificate',
                component: () => import('../Views/Employ/Certificate/SingleCertificate.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Enskilt certifikat',
                },
                beforeEnter: (to) => {
                    if (to.params.certificateToken.length <= 0) {
                        return { name: '404' };
                    }
                },
                props(route) {
                    return {
                        id: route.params.certificateToken,
                    };
                },
            },
            {
                path: 'certificate/single/:certificateToken',
                name: 'employ-external-single-certificate',
                component: () => import('../Views/Certificate/SingleCertificate.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Enskilt certifikat',
                },
                beforeEnter: (to) => {
                    if (to.params.certificateToken.length <= 0) {
                        return { name: '404' };
                    }
                },
                props(route) {
                    return {
                        id: route.params.certificateToken,
                    };
                },
            },
            {
                path: 'employees/:id',
                name: 'employ-user-admin-view',
                component: () => import('../Views/Employ/Employee/AdminView.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Användare',
                },
                props(route) {
                    return {
                        id: route.params.id,
                    };
                },
            },
            {
                path: 'competence',
                name: 'employ-competences',
                component: () => import('../Views/Employ/Competence/Competence.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Kompetenser',
                },
            },
            {
                path: 'employees',
                name: 'employ-employees',
                component: () => import('../Views/Employ/Employees.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Medarbetare',
                },
            },
            {
                path: 'employees/report',
                name: 'employ-employees-report',
                component: () => import('../Views/Employ/Employee/EmployeeReport.vue'),
                meta: {
                    title: 'Skapa rapport',
                    requiresAuth: true,
                },
            },
            {
                path: 'settings',
                name: 'employ-settings',
                component: () => import('../Views/Employ/Settings.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Företags- och certifikatsuppgifter',
                },
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from) {
        // Navigating on the same page with anchor links
        // don't disturb the user with scroll top and smooth scroll
        if (to.path === from.path) {
            return false;
        }

        if (to.hash) {
            return {
                el: to.hash,
                behavior: 'smooth',
            };
        } else {
            return { top: 0 };
        }
    },
});

/**
 * Check if an authenticated user can access a given route.
 * @returns {Boolean}
 */
const canUserAccess = (to, profile, department = null) => {
    const requiredRoles = [];
    const { system_role } = profile;

    /**
     * Certificate routes
     *
     * Requires Certifier
     * Exception: Single certificate and verify certificate
     */
    if (to.path.match(/^\/(certificate)\/?/)) {
        if (-1 === ['single-certificate', 'verify-certificate'].indexOf(to.name)) {
            requiredRoles.push(ROLES.CERTIFIER);
        }
    }

    /**
     * Superadmin routes
     *
     * Requires Superadmin
     */
    if (to.path.match(/^\/(superadmin)\/?/)) {
        requiredRoles.push(ROLES.SUPER_ADMIN);
    }

    /**
     * Department routes
     *
     * Requires Organization admin or Certifier
     * Exception: Organization admins does not have access to users and clients
     */
    if (to.path.match(/^\/(department)\/?/)) {
        requiredRoles.push(ROLES.CERTIFIER);
        if (-1 !== ['payment'].indexOf(to.name)) {
            requiredRoles.push(ROLES.ORGANIZATION_ADMIN);
        }
    }

    /**
     * Employ routes
     *
     * Requires Organization admin
     * Exception: Do not include registration routes
     */
    if (to.path.match(/^\/(employ)\/?/)) {
        if (-1 === ['employ-register', 'employ-select-plan'].indexOf(to.name)) {
            requiredRoles.push(ROLES.ORGANIZATION_TEAM_ADMIN);
            requiredRoles.push(ROLES.ORGANIZATION_ADMIN);
        }
    }

    return (
        0 === requiredRoles.length ||
        -1 !== requiredRoles.indexOf(system_role.id) ||
        (department && -1 !== requiredRoles.indexOf(department.user_role?.id))
    );
};

/**
 * Returns the default route name for a given user profile.
 * @returns {String}
 */
const getRouteForUserProfile = (profile, department = null) => {
    const { system_role } = profile;
    let routeName = 'account';

    if (system_role.id === ROLES.SUPER_ADMIN) {
        return 'superadmin';
    }

    if (department) {
        const departmentRole = department.user_role;
        if (department.type === 'educate') {
            switch (departmentRole?.id) {
                case ROLES.CERTIFIER:
                    routeName = 'certificate';
                    break;
                case ROLES.ORGANIZATION_ADMIN:
                    routeName = 'certificate';
                    break;
                default:
                    routeName = 'account';
                    break;
            }
        }

        if (department.type === 'employ') {
            switch (departmentRole?.id) {
                case ROLES.CERTIFIER:
                    routeName = 'employ-dashboard';
                    break;
                case ROLES.ORGANIZATION_ADMIN:
                    routeName = 'employ-dashboard';
                    break;
                default:
                    routeName = 'account';
                    break;
            }
        }
    }

    return routeName;
};
/**
 * Check if the department can access the system
 *
 * @param to
 * @param department
 * @returns {*|boolean}
 */
const canDepartmentAccess = (to, department) => {
    return !(
        department &&
        department?.has_incomplete_billing_details &&
        !to.name.match(/(account|payment|login|register|single-certificate)/)
    );
};

/**
 * Check if the department can access the system
 *
 * @param cache
 * @returns {*|boolean}
 */
const hasIncompleteProfile = (cache) => {
    return cache && cache.incomplete_user;
};

router.beforeEach((to, from) => {
    if ('404' === to.name) {
        return;
    }

    const cache = getCache();

    // Fallback if current profile invalid
    if (cache && !cache?.current_profile) {
        cache.current_profile = {
            system_role: {
                name: 'Guest',
                id: ROLES.GUEST,
            },
        };
    }

    /**
     * GUARDS
     */

    // Does the current route require an authenticated user?
    const { requiresAuth } = to.meta;

    if (authUser()) {
        if ('redirect' in to.query) {
            const { redirect } = to.query;
            delete to.query.redirect;
            return { path: decodeURIComponent(redirect + ''), query: to.query, hash: to.hash };
        }
        if ('login' === to.name) {
            return { name: getRouteForUserProfile(cache.current_profile, cache?.currentDepartment) };
        }
        if ('accept-department-invite' === to.name && !hasIncompleteProfile(cache)) {
            if (removeCache()) {
                // Delete the session and restart
                deleteCookie('cervd_session');
                location.reload();
            }

            // dispatchSystemError({
            //     message:
            //         'Du kan inte acceptera en inbjudan från ett annat konto när du är redan inloggad. Logga ut först och försök igen.',
            //     status: 'Error',
            //     btnText: 'Logga ut',
            //     btnHref: () => {
            //         logoutUser().then(() => {
            //             if (removeCache()) {
            //                 deleteCookie('cervd_session');
            //                 location.reload();
            //             }
            //         });
            //     },
            // });
            // return { name: 'account' };
        }
        // Is the current user authorized to access the view?
        if (!canUserAccess(to, cache.current_profile, cache?.currentDepartment)) {
            // if (cache.current_profile.system_role.id !== ROLES.SUPER_ADMIN) {
            // dispatchSystemError(
            //     'Vi har omdirigerat dig till din startsida eftersom du inte har behörighet att komma åt den sidan.'
            // );
            // }
            return from.name
                ? false
                : { name: getRouteForUserProfile(cache.current_profile, cache?.currentDepartment) };
        }

        // Has the current department set their billing details?
        if (!canDepartmentAccess(to, cache.currentDepartment)) {
            dispatchSystemError('Du måste ange dina betalningsuppgifter innan du kan fortsätta.');
            return { name: 'payment', hash: '#billing' };
        }
        // Force the user to complete their profile
        if (hasIncompleteProfile(cache)) {
            if (to.name !== 'login-complete-profile' && to.name !== 'accept-department-invite') {
                return { name: 'login-complete-profile' };
                // return { name: 'login-complete-profile', query: { redirect: encodeURIComponent(to.fullPath) } };
            }
            // dispatchSystemError('Du måste kompletera dina personuppgifter innan du kan fortsätta.');
            // if (to.name !== 'account' || to.hash !== '#detailsTab') {
            //     return { name: 'account', hash: '#detailsTab' };
            // }
        }
    } else if (requiresAuth && 'login' !== to.name) {
        return { name: 'login', query: { redirect: encodeURIComponent(to.fullPath) } };
    }
});

router.afterEach((to) => {
    // Update page title
    document.title = `Falagrett - ${to.meta.title}` || '';
});

export default router;
